export function countableLabel(singularWord: string = '', options: object = {}) {
  const defaults = {
    allPrefix: 'All',
    compact: false,
    emptyLabel: 'None',
    nonePrefix: 'No',
    selected: 0,
    total: 0,
  };
  const opts = { ...defaults, ...options };
  const { compact, emptyLabel, selected, total, allPrefix, nonePrefix } = opts;

  // This is obviously pretty dumb so when no longer sufficient,
  // check out https://github.com/blakeembrey/pluralize/blob/master/pluralize.js or similar
  const pluralizedWord = `${singularWord}s`;

  if (!singularWord) {
    return '';
  }

  if (selected === 0) {
    if (compact) {
      return emptyLabel;
    }
    return `${nonePrefix} ${pluralizedWord}`;
  }

  if (selected === total) {
    if (compact) {
      return allPrefix;
    }
    return `${allPrefix} ${pluralizedWord}`;
  }

  if (selected === 1) {
    if (compact) {
      return `${selected}/${total}`;
    }
    return `${selected} ${singularWord}`;
  }

  // if selected > 1
  if (compact) {
    return `${selected}/${total}`;
  }
  return `${selected} ${pluralizedWord}`;
}

export interface PluralizeOptions {
  singular: string;
  plural: string;
  none?: string;
  /** Omit amount if you only need the pluralized word returned */
  omitAmount?: boolean;
}
export function pluralize(
  amount: number,
  { singular, plural, none, omitAmount = false }: PluralizeOptions
): string {
  if (amount === 1) {
    return omitAmount ? singular : `${amount} ${singular}`;
  }
  if (amount === 0 && none != null) {
    return none;
  }
  return omitAmount ? plural : `${amount} ${plural}`;
}

export function stripMentionMarkup(source: string) {
  if (!source) {
    return source;
  }

  // params: match, name, type, offset, string
  return source.replace(/@\[([^\]]*)\]\(([^\]]*)\)/g, (match, name, type) => {
    switch (type) {
      case 'user':
        if (name.substr(0, 1) === '@') {
          return name;
        }
        return `@${name}`;
      default:
        return name;
    }
  });
}

export function ucFirst(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const capitalize = ucFirst;

// Lifted from https://gist.github.com/mathewbyrne/1280286,
// look at npm's slugify & friends if we need more advanced functionality.
export const slugify = (text: string) => {
  // Use hash map for special characters
  const specialChars: Record<string, string> = {
    à: 'a',
    ä: 'a',
    á: 'a',
    â: 'a',
    æ: 'a',
    å: 'a',
    ë: 'e',
    è: 'e',
    é: 'e',
    ê: 'e',
    î: 'i',
    ï: 'i',
    ì: 'i',
    í: 'i',
    ò: 'o',
    ó: 'o',
    ö: 'o',
    ô: 'o',
    ø: 'o',
    ù: 'o',
    ú: 'u',
    ü: 'u',
    û: 'u',
    ñ: 'n',
    ç: 'c',
    ß: 's',
    ÿ: 'y',
    œ: 'o',
    ŕ: 'r',
    ś: 's',
    ń: 'n',
    ṕ: 'p',
    ẃ: 'w',
    ǵ: 'g',
    ǹ: 'n',
    ḿ: 'm',
    ǘ: 'u',
    ẍ: 'x',
    ź: 'z',
    ḧ: 'h',
    '·': '-',
    '/': '-',
    _: '-',
    ',': '-',
    ':': '-',
    ';': '-',
  };

  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/./g, target => specialChars[target] || target) // Replace special characters using the hash map
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};

export const truncateText = (stringValue: string, maxChars: number, ellipsis: string = '...') => {
  if (stringValue.length < maxChars) {
    return stringValue;
  }
  const charsInEllipsis = ellipsis.length || 0;
  return `${stringValue.substring(0, maxChars - charsInEllipsis)}${ellipsis}`;
};
